import { useEffect, useRef } from "react";
import {
  createReconnectState,
  decideAfterClose,
  notifyMessage,
  notifyOpened,
  refreshWsToken,
} from "@/lib/wsReconnect";
import { toastError, toastId, toastSuccess, toastWarning } from "@/lib/toast";
import { translateNotification } from "@/features/notifications/lib/translateNotification";
import {
  notificationOutcome,
  type NotificationOutcome,
} from "@/features/notifications/lib/notificationOutcome";
import type { AdminAlertSeverity } from "@/types/admin";
import { useSetAtom } from "jotai";
import { optimizationBatchAtom } from "@/store/optimizationBatch";
import { fetchActiveBatch } from "@/api/planning";
import i18next from "@/lib/i18n";
import {
  parseBuildResultNotification,
  showBuildFailureToast,
  showBuildResultToast,
} from "@/features/routes/lib/buildResultToast";

interface NotificationMessage {
  type: string;
  id?: number;
  title?: string;
  body?: string;
  notification_type?: string;
  created_at?: string;
  extra_data?: Record<string, unknown> | null;
  /**
   * Severity bucket, when the backend supplies one. Today only broadcast rows
   * carry it; TASK-4743's backend half will start populating it on failure rows
   * too, at which point `notificationOutcome` already routes them.
   */
  severity?: AdminAlertSeverity | null;
  batch_id?: string;
  done?: number;
  total?: number;
  failed?: number;
  // `chat.thread_updated` (BUG-723)
  thread_id?: number;
  reason?: string;
  message_id?: number | null;
  unread_dispatcher_count?: number;
  unread_total?: number;
  last_message_at?: string | null;
  // `notification.read` (BUG-723)
  ids?: number[];
  unread_count?: number;
}

/** `chat.thread_updated` — a thread changed somewhere in the tenant (BUG-723). */
export interface ChatThreadUpdatedEvent {
  thread_id: number;
  reason: "message" | "forwarded" | "deleted" | "read" | string;
  message_id: number | null;
  unread_dispatcher_count: number;
  /** Equals `GET /chat/unread-count/` — the sidebar «Чат» badge. */
  unread_total: number;
  last_message_at: string | null;
}

/** `notification.read` — bell rows this user just read elsewhere (BUG-723). */
export interface NotificationsReadEvent {
  ids: number[];
  unread_count: number;
}

/**
 * Auto-dismiss window for a routine bell toast (BUG-321). Long enough to read,
 * short enough that a burst does not linger. Persistent (Infinity) toasts are
 * reserved for genuinely blocking events and are intentionally not used here.
 */
const NOTIFICATION_TOAST_DURATION_MS = 6_000;

interface Options {
  /**
   * Called whenever a `notification.new` WS message is received.
   * Use this to invalidate TanStack Query caches or trigger other side-effects.
   */
  onNewNotification?: () => void;
  /**
   * Called when an `optimization.batch_done` WS message is received.
   * Use this to invalidate route-related caches so the UI reflects recalculated data.
   */
  onBatchDone?: () => void;
  /** A chat thread changed (new / forwarded / deleted message, read). */
  onChatThreadUpdated?: (event: ChatThreadUpdatedEvent) => void;
  /** Bell notifications were marked read for this user (thread read). */
  onNotificationsRead?: (event: NotificationsReadEvent) => void;
}

/**
 * Opens a persistent WebSocket connection to the backend notifications endpoint.
 * Shows a finite-duration, per-type coalescing toast whenever a
 * `notification.new` message is received (BUG-321).
 * Tracks batch optimization progress via `optimization.progress` and
 * `optimization.batch_done` events (stored in optimizationBatchAtom).
 *
 * Reconnect policy lives in `@/lib/wsReconnect` and is shared with the tracking
 * and chat hooks (TASK-4987): jittered exponential backoff 1 s → 30 s, a stop on
 * 4001/4003, and exactly ONE token refresh on 4401 before giving up. The backoff
 * is reset only by a connection that proves usable — never by a bare `onopen`,
 * which the server fires even when it is about to refuse the socket (BUG-580).
 */
export default function useNotificationsWebSocket({
  onNewNotification,
  onBatchDone,
  onChatThreadUpdated,
  onNotificationsRead,
}: Options = {}): void {
  const mountedRef = useRef(true);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectRef = useRef(createReconnectState());

  const onNewNotificationRef = useRef(onNewNotification);
  onNewNotificationRef.current = onNewNotification;

  const onBatchDoneRef = useRef(onBatchDone);
  onBatchDoneRef.current = onBatchDone;

  const onChatThreadUpdatedRef = useRef(onChatThreadUpdated);
  onChatThreadUpdatedRef.current = onChatThreadUpdated;

  const onNotificationsReadRef = useRef(onNotificationsRead);
  onNotificationsReadRef.current = onNotificationsRead;

  const setBatch = useSetAtom(optimizationBatchAtom);

  useEffect(() => {
    mountedRef.current = true;
    reconnectRef.current = createReconnectState();

    // Do not fire any authenticated request or open the socket while logged
    // out (e.g. on the /login page) — there is no auth token to authorize them
    // (BUG-175).
    if (!localStorage.getItem("auth_token")) {
      return () => {
        mountedRef.current = false;
      };
    }

    // Restore active batch state after page refresh
    fetchActiveBatch()
      .then((data) => {
        if (!mountedRef.current) return;
        if (data.batch_id && data.total > 0 && data.done < data.total) {
          setBatch({
            batchId: data.batch_id,
            done: data.done,
            total: data.total,
          });
        }
      })
      .catch(() => {});

    function connect() {
      if (!mountedRef.current) return;

      const token = localStorage.getItem("auth_token");
      if (!token) return;

      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${proto}//${window.location.host}/ws/notifications/?token=${token}`;

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        // Deliberately does NOT reset the backoff — see `notifyOpened`.
        notifyOpened(reconnectRef.current, Date.now());
        // Re-sync batch state after reconnect — clears stale banner if batch finished
        // while disconnected, or restores in-progress banner if batch is still active.
        fetchActiveBatch()
          .then((data) => {
            if (!mountedRef.current) return;
            if (data.batch_id && data.total > 0 && data.done < data.total) {
              setBatch({
                batchId: data.batch_id,
                done: data.done,
                total: data.total,
              });
            } else {
              setBatch(null);
            }
          })
          .catch(() => {});
      };

      ws.onmessage = (event) => {
        // A frame is the proof that the connection works, so THIS is what
        // resets the backoff.
        notifyMessage(reconnectRef.current);
        try {
          const msg = JSON.parse(event.data as string) as NotificationMessage;

          if (msg.type === "notification.new") {
            // BUG-676: a coded route-build result shares ONE toast with the
            // launching tab's poller (`buildResultToast.tsx`) — styled by
            // outcome, never red for a build that created routes, and never a
            // second toast next to the poller's for the same build.
            const buildResult =
              msg.notification_type === "routes_built"
                ? parseBuildResultNotification(msg.extra_data)
                : null;
            if (buildResult) {
              showBuildResultToast(buildResult, "notification", i18next.t);
              onNewNotificationRef.current?.();
              return;
            }
            // BUG-679: a FAILED build shares one toast with the poller's
            // failure toast, the more specific wording winning.
            if (msg.notification_type === "routes_build_failed") {
              showBuildFailureToast(msg.extra_data, "notification");
              onNewNotificationRef.current?.();
              return;
            }
            // Localize keyed on notification_type, falling back to raw
            // title/body for untranslated types (BUG-322).
            const { title, body } = translateNotification(msg);
            // Route by OUTCOME, not unconditionally to success (TASK-4743).
            // A failure ("Не удалось построить маршруты") used to pop as a green
            // toastSuccess, which tells the dispatcher the exact opposite of what
            // happened. The predicate is shared with NotificationBell so a row
            // flagged as a failure in the bell can never toast green.
            const outcome = notificationOutcome(msg);
            // Coalesce: a stable id makes sonner REPLACE the prior toast of the
            // same kind instead of stacking, and a finite duration lets toasts
            // self-dismiss — so a fan-out burst no longer buries the screen
            // (BUG-321). The id is built with `toastId`, so the OUTCOME is part
            // of it: previously `notification-${notification_type}` was
            // outcome-blind and a success silently REPLACED a failure of the same
            // type (both ride ROUTES_BUILT / ROUTE_CHANGED), leaving a batch's
            // failures invisible. Coalescing within one outcome still applies.
            const id = toastId(
              outcome,
              "notification",
              msg.notification_type ?? "generic",
            );
            // A failure toast keeps the flavour default (10s, ERROR_TOAST_DURATION_MS)
            // — the dispatcher has to READ which orders broke — while routine
            // successes keep the short BUG-321 window.
            const toastFor: Record<NotificationOutcome, typeof toastSuccess> = {
              success: toastSuccess,
              warning: toastWarning,
              error: toastError,
            };
            toastFor[outcome](title, body, {
              id,
              ...(outcome === "success"
                ? { duration: NOTIFICATION_TOAST_DURATION_MS }
                : {}),
            });
            onNewNotificationRef.current?.();
          } else if (msg.type === "optimization.progress") {
            setBatch({
              batchId: msg.batch_id!,
              done: msg.done!,
              total: msg.total!,
            });
          } else if (msg.type === "chat.thread_updated") {
            if (typeof msg.thread_id === "number") {
              onChatThreadUpdatedRef.current?.({
                thread_id: msg.thread_id,
                reason: msg.reason ?? "message",
                message_id: msg.message_id ?? null,
                unread_dispatcher_count: msg.unread_dispatcher_count ?? 0,
                unread_total: msg.unread_total ?? 0,
                last_message_at: msg.last_message_at ?? null,
              });
            }
          } else if (msg.type === "notification.read") {
            onNotificationsReadRef.current?.({
              ids: Array.isArray(msg.ids) ? msg.ids : [],
              unread_count: msg.unread_count ?? 0,
            });
          } else if (msg.type === "optimization.batch_done") {
            // Clear the progress bar
            setBatch(null);
            onNewNotificationRef.current?.();
            onBatchDoneRef.current?.();
          }
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onerror = () => {
        // A socket that errors mid-handshake fires `onclose` on its own, and
        // calling close() while still CONNECTING emits the browser warning
        // "WebSocket is closed before the connection is established" (BUG-181).
        // Only force-close an already-open socket; let a failed handshake close
        // itself and drive reconnect via onclose.
        if (ws.readyState !== WebSocket.CONNECTING) {
          ws.close();
        }
      };

      ws.onclose = (event) => {
        wsRef.current = null;

        if (!mountedRef.current) return;

        const decision = decideAfterClose(
          reconnectRef.current,
          event.code,
          Date.now(),
          Math.random,
        );

        if (decision.action === "stop") {
          console.warn(
            `[useNotificationsWebSocket] Closed with code ${event.code} (${decision.reason}). Stopping reconnection.`,
          );
          return;
        }

        if (decision.action === "refresh") {
          void refreshWsToken().then((token) => {
            if (!mountedRef.current) return;
            // A failed refresh stops for good rather than falling back into the
            // retry loop — that fallback is the BUG-580 behaviour.
            if (token === null) return;
            connect();
          });
          return;
        }

        reconnectTimerRef.current = setTimeout(connect, decision.delayMs);
      };
    }

    connect();

    return () => {
      mountedRef.current = false;

      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      const ws = wsRef.current;
      if (ws !== null) {
        // Detach all handlers so no reconnect/message/error logic fires after
        // unmount (preserves the onclose-null reconnect guard).
        ws.onclose = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onopen = null;
        if (ws.readyState === WebSocket.CONNECTING) {
          // Calling close() on a still-CONNECTING socket emits the browser
          // warning "WebSocket is closed before the connection is established"
          // (BUG-180) — and a deferred onopen-close never fires if the
          // handshake fails, so the socket dies CONNECTING and warns anyway.
          // With every handler detached, abandon the socket: the browser tears
          // down the orphaned connection on its own, with no warning and no
          // reconnect/message logic possible.
        } else {
          ws.close();
        }
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
